import React, { useState, useEffect } from 'react';
import { Container, Button, Box, CircularProgress, Alert } from '@mui/material';
import CipherChallenge from '../components/CipherChallenge';
import { getRandomQuote, vigenereEncode, generateVigenereKey } from '../utils/cipherUtils';

const VigenereCipher = () => {
  const [challenge, setChallenge] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [correctKey, setCorrectKey] = useState(null);
  const [error, setError] = useState(null);

  const generateNewChallenge = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const quote = await getRandomQuote();
      if (quote) {
        // Generate a random key length between 4 and 8
        const keyLength = Math.floor(Math.random() * 5) + 4;
        const key = generateVigenereKey(keyLength);
        setCorrectKey(key);
        setChallenge({
          encodedMessage: vigenereEncode(quote.text, key),
          correctAnswer: quote.text.replace(/[^a-zA-Z]/g, '').toLowerCase(),
          originalText: quote.text,
          hint: `A keyword is used to shift each letter. The key is provided below.`,
          difficulty: quote.difficulty
        });
      }
    } catch (error) {
      console.error('Error generating challenge:', error);
      setError('Failed to load challenge. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    generateNewChallenge();
  }, []);

  const handleComplete = (result) => {
    if (result.success) {
      generateNewChallenge();
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Button
            variant="contained"
            color="primary"
            onClick={generateNewChallenge}
            sx={{ mt: 2 }}
          >
            Try Again
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      {challenge && (
        <>
          <CipherChallenge
            title="Vigenère Cipher Challenge"
            description="In a Vigenère cipher, each letter in the alphabet is shifted by an amount determined by a repeating keyword. This makes it much harder to break than a Caesar cipher!"
            encodedMessage={challenge.encodedMessage}
            correctAnswer={challenge.correctAnswer}
            originalText={challenge.originalText}
            hint={challenge.hint}
            difficulty={challenge.difficulty}
            onComplete={handleComplete}
            autoFillSameLetters={false}
            allowDuplicateLetters={false}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              The keyword for this cipher is: <b>{correctKey}</b>
            </Alert>
          </Box>
          
        </>
      )}
    </Container>
  );
};

export default VigenereCipher; 